/**
 * SUB-AUTO-RESEND (Seth 2026-09-25, "Proceed with the ideas") — when an install
 * that was already sent to the subcontractor MOVES (or loses its date), the
 * person who moved it is asked right then: "Powerhouse Electric has the old
 * date — send the new one?" The job page keeps the same warning + next step as
 * the safety net, so a move made anywhere (the board, the job, the phone) is
 * never silently missed.
 *
 * Competitor frame: Jobber and Housecall Pro ask "notify the client?" the
 * moment a visit is rescheduled; ServiceTitan re-sends appointment changes to
 * the customer. None of them tell a subcontractor — here the sub is the one
 * who shows up at the wrong house on the wrong day. Adopt the ask-at-the-moment
 * pattern; never auto-send (a dispatcher dragging dates around while planning
 * would spam the sub).
 *
 * This module is the store + the pure "what does the sub have?" logic; it
 * reads the database directly (no jobs.ts import) so any feature can call
 * `checkSubResend` after it moves a visit.
 */
import { create } from "zustand";
import { supabase } from "@/lib/supabase";

export interface SendLite {
  to_name: string | null;
  status: string;
  kind?: string | null;
  install_date: string | null;
  install_window: string | null;
}
export interface DayLite { date: string; window: string | null }

/**
 *  none        — nothing sent yet
 *  current     — the sub has the date the job has
 *  moved       — the job's install day / window differs from what they were told
 *  unscheduled — they were told a day, and the install has no date now
 *  needs_date  — they were told it's postponed, and it's scheduled again
 */
export type SubSendState = "none" | "current" | "moved" | "unscheduled" | "needs_date";

export function subSendState(last: SendLite | null | undefined, first: DayLite | null): SubSendState {
  if (!last) return "none";
  if ((last.kind ?? "work_order") === "postponed") return first ? "needs_date" : "current";
  if (!first) return last.install_date ? "unscheduled" : "current";
  if ((last.install_date ?? null) !== first.date || (last.install_window ?? null) !== (first.window ?? null)) return "moved";
  return "current";
}

/** The first dated install visit (else any dated visit). */
export function firstDayOf(visits: { scheduled_date: string | null; scheduled_window: string | null; visit_role: string | null }[]): DayLite | null {
  const dated = visits.filter((v) => !!v.scheduled_date);
  const inst = dated.filter((v) => v.visit_role === "install");
  const pick = (inst.length ? inst : dated).map((v) => ({ date: v.scheduled_date as string, window: v.scheduled_window }))
    .sort((a, b) => a.date.localeCompare(b.date))[0];
  return pick ?? null;
}

export interface ResendPrompt {
  jobId: string;
  subName: string;
  state: Exclude<SubSendState, "none" | "current">;
  was: DayLite | null;
  now: DayLite | null;
}

interface Store {
  prompts: ResendPrompt[];
  push: (p: ResendPrompt) => void;
  dismiss: (jobId: string) => void;
}
export const useResendPrompts = create<Store>((set) => ({
  prompts: [],
  push: (p) => set((s) => ({ prompts: [...s.prompts.filter((x) => x.jobId !== p.jobId), p].slice(-3) })),
  dismiss: (jobId) => set((s) => ({ prompts: s.prompts.filter((x) => x.jobId !== jobId) })),
}));

/**
 * After a visit moves: does its job's sub now have the wrong day? If so, ask.
 * Pass the visit (dispatch) id, or the job id. Never throws — a failed check
 * must not fail the move that called it.
 */
export async function checkSubResend(ref: { dispatchId?: string | null; jobId?: string | null }): Promise<void> {
  try {
    let jobId = ref.jobId ?? null;
    if (!jobId && ref.dispatchId) {
      const { data } = await supabase.from("pegasus_dispatch").select("job_id").eq("id", ref.dispatchId).maybeSingle();
      jobId = (data as { job_id: string | null } | null)?.job_id ?? null;
    }
    if (!jobId) return;
    const [{ data: sends }, { data: visits }] = await Promise.all([
      supabase.from("pegasus_job_send").select("to_name, status, kind, install_date, install_window")
        .eq("job_id", jobId).order("sent_at", { ascending: false }).limit(1),
      supabase.from("pegasus_dispatch").select("scheduled_date, scheduled_window, visit_role")
        .eq("job_id", jobId).is("deleted_at", null),
    ]);
    const last = ((sends ?? []) as SendLite[])[0] ?? null;
    const first = firstDayOf((visits ?? []) as { scheduled_date: string | null; scheduled_window: string | null; visit_role: string | null }[]);
    const state = subSendState(last, first);
    const store = useResendPrompts.getState();
    if (state === "none" || state === "current") { store.dismiss(jobId); return; }
    store.push({
      jobId,
      subName: last?.to_name ?? "The subcontractor",
      state,
      was: last?.install_date ? { date: last.install_date, window: last.install_window } : null,
      now: first,
    });
  } catch {
    // best-effort — the job page shows the same warning
  }
}
